import { call, put, select, takeLatest, takeLeading } from "redux-saga/effects";
import { CustomerAPI } from "services/customer";
import {
  getProfile,
  getAccounts,
  getProfileSuccess,
  getProfileFailure,
  getAccountsSuccess,
  getAccountsFailure,
  getUhmfList,
  getUhmfListSuccess,
  getUhmfListFailure,
  cancelFetchUhmfList,
  getSuitabilityData,
  getSuitabilityDataSuccess,
  getSuitabilityDataFailure,
  setSuitabilityResult,
  checkSuitability,
  getAccountsFCY,
  getAccountsFCYSuccess,
  getAccountsFCYFailure,
  getGlobalAccounts,
  getGlobalAccountsSuccess,
  getGlobalAccountsFailure,
  checkUserType,
  checkUserTypeSuccess,
  getPrimaryLombardAccounts,
  getSecondaryLombardAccounts,
  passBankruptcyValidate,
} from "redux/reducers/customer";
import { ProductSelector } from "redux/selector";
import { isTrue } from "utils/common-util";
import { CustomerSelector } from "redux/selector/customer-selector";
import { BuySellSwitchAPI } from "services/buy-sell-switching";
import GTM from "utils/gtm";
import { RiskProfileError } from "classes/error/RiskProfileError";
import { openModal } from "redux/reducers/central-modal";
import { RiskProfileErrors } from "const";
import { oAuthStorage } from "utils/o-auth-utils";

export function* getProfileSaga() {
  try {
    const response = yield call(CustomerAPI.getCustomerProfileCBS);
    yield put(getProfileSuccess(response.data.result));
    return true;
  } catch (error) {
    yield put(getProfileFailure(error));
    return false;
  }
}

function* getAccountsSaga(action) {
  try {
    const accounts = yield select(CustomerSelector.accounts);
    const noRefresh = isTrue(action.payload?.noRefresh) && accounts?.length > 0;
    if (!noRefresh) {
      let response;
      if (action.payload?.provider === "PWM") {
        response = yield call(BuySellSwitchAPI.getAccountList);
      } else {
        const securityCode =
          action.payload?.securityCode ?? (yield select(ProductSelector.productDetail))?.securityCode;
        response = yield call(CustomerAPI.getCustomerAccounts, { securityCode });
      }
      const accounts = (response.data?.result?.accounts ?? []).filter((account) => !!account.accountNo);
      yield put(getAccountsSuccess(accounts));
    }
  } catch (error) {
    yield put(getAccountsFailure(error));
  }
}

function* getPrimaryLombardAccountsSaga() {
  try {
    const securityCode = (yield select(ProductSelector.productDetail))?.securityCode;
    const response = yield call(CustomerAPI.getCustomerAccounts, { securityCode, lombardFlag: "Y" });
    const accounts = (response.data?.result?.accounts ?? []).filter((account) => !!account.accountNo);
    yield put(getAccountsSuccess(accounts));
  } catch (error) {
    yield put(getAccountsFailure(error));
  }
}

function* getAccountsFCYSaga(action) {
  try {
    const accountsFCY = yield select(CustomerSelector.accountsFCY);
    const noRefresh = isTrue(action.payload?.noRefresh) && accountsFCY?.length > 0;
    if (!noRefresh) {
      const securityCode = action.payload?.securityCode ?? (yield select(ProductSelector.productDetail))?.securityCode;
      const response = yield call(CustomerAPI.getCustomerFCYAccounts, { securityCode });
      const accounts = (response.data?.result?.accounts ?? []).filter((account) => !!account.accountNo);
      yield put(getAccountsFCYSuccess(accounts));
    }
  } catch (error) {
    yield put(getAccountsFCYFailure(error));
  }
}

function* getSecondaryLombardAccountsSaga() {
  try {
    const response = yield call(CustomerAPI.getGlobalCustomerAccounts, { lombardFlag: "Y" });
    const accounts = (response.data?.result?.accountList ?? [])
      .filter((account) => !!account.accountNo)
      .map((account) => {
        account.isLombardAccount = true;
        return account;
      });
    yield put(getGlobalAccountsSuccess(accounts));
  } catch (error) {
    yield put(getGlobalAccountsFailure(error));
    yield put(getGlobalAccounts());
  }
}

function* getGlobalAccountsSaga(action) {
  try {
    const globalAccounts = yield select(CustomerSelector.globalAccounts);
    const noRefresh = isTrue(action.payload?.noRefresh) && globalAccounts?.length > 0;
    if (!noRefresh) {
      const response = yield call(CustomerAPI.getGlobalCustomerAccounts);
      const accounts = (response.data?.result?.accountList ?? []).filter((account) => !!account.accountNo);
      yield put(getGlobalAccountsSuccess(accounts));
    }
  } catch (error) {
    yield put(getGlobalAccountsFailure(error));
  }
}

function* getUhmfListSaga(action) {
  try {
    const uhmfList = yield select(CustomerSelector.uhmfList);
    const noRefresh = isTrue(action.payload?.noRefresh) && uhmfList?.length > 0;
    if (!noRefresh) {
      const response = yield call(BuySellSwitchAPI.getUHMF);
      yield put(getUhmfListSuccess(response.data?.result?.uhmfList ?? []));
    } else {
      yield put(cancelFetchUhmfList());
    }
  } catch (error) {
    yield put(getUhmfListFailure(error));
  }
}

function* getSuitabilityDataSaga({ payload }) {
  try {
    const response = yield call(CustomerAPI.getSuitabilityData, payload?.signal);
    const suitability = response.data.result;
    yield put(getSuitabilityDataSuccess(suitability));
  } catch (error) {
    if (!error.isAborted) {
      yield put(openModal({ code: RiskProfileErrors.SystemNotReady }));
    }
    yield put(getSuitabilityDataFailure({ ...error, ignore: true }));
  }
}

export function* checkRiskProfile() {
  let validSuitability = yield select(CustomerSelector.suitability);

  if (validSuitability === null) {
    try {
      const response = yield call(CustomerAPI.suitability);
      validSuitability = !response.data?.result?.suitabilityTest;
      yield put(setSuitabilityResult(validSuitability));
    } catch (error) {
      throw new RiskProfileError();
    }
  }

  return validSuitability;
}

function logUserLoginToGA(userInfo) {
  const { uhnw, gmCustomer } = userInfo;
  GTM.dataLayer({
    event: "user_login",
    user_type: gmCustomer ? "PWM_USER" : uhnw ? "UHNW_USER" : "RETAIL_USER",
    channel: oAuthStorage.channel,
  });
}

export function* checkUserTypeSaga() {
  let userInfo = {};
  try {
    const fetchUserTypeOnce = yield select(CustomerSelector.fetchUserTypeOnce);
    const response = yield call(CustomerAPI.isUserUHNW);
    userInfo = response.data.result;
    yield put(checkUserTypeSuccess(userInfo));
    if (!fetchUserTypeOnce) logUserLoginToGA(userInfo); // log once per login
  } catch (error) {
    yield put(getSuitabilityDataFailure(error));
  }
  return userInfo;
}

export function* isPassedTheBankruptcyQualificationCheck() {
  const bankruptcyPassed = yield select(CustomerSelector.bankruptcyPassed);

  if (bankruptcyPassed) {
    return true;
  }

  const response = yield call(CustomerAPI.checkBankruptcy);
  const passed = !response.data.result.bankrupt;

  if (passed) {
    yield put(passBankruptcyValidate());
  }

  return passed;
}

const saga = [
  takeLatest(getProfile.type, getProfileSaga),
  takeLeading(getAccounts.type, getAccountsSaga),
  takeLeading(getPrimaryLombardAccounts.type, getPrimaryLombardAccountsSaga),
  takeLeading(getAccountsFCY.type, getAccountsFCYSaga),
  takeLeading(getSecondaryLombardAccounts.type, getSecondaryLombardAccountsSaga),
  takeLeading(getGlobalAccounts.type, getGlobalAccountsSaga),
  takeLeading(getUhmfList.type, getUhmfListSaga),
  takeLatest(getSuitabilityData.type, getSuitabilityDataSaga),
  takeLeading(checkSuitability.type, checkRiskProfile),
  takeLeading(checkUserType.type, checkUserTypeSaga),
];

export default saga;
